/**
 * UniversalTable Component
 *
 * Universal, backend-driven table component for all collections
 * Similar to UniversalRULForm - receives all instructions from backend
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table, Input, Button, Space, Tag, Spin, Alert, Badge } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { useUDCTable, useUDCSelection } from '@/hooks/useUDC';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { FilterBuilder } from './FilterBuilder';
import { ActiveFilters } from './ActiveFilters';
import { ColumnSelector } from './ColumnSelector';
import { BulkActionBar } from './BulkActionBar';
import { TableSkeleton } from '@/components/common/skeletons/TableSkeleton';
import type { UDCColumnDefinition, UDCFilter } from '@/api/types/udc.types';
import type { ColumnsType } from 'antd/es/table';
import './UniversalTable.css';

interface UniversalTableProps {
  /** Collection name (e.g., 'tickets', 'leads', 'bp') */
  collection: string;
  /** UDC profile name (e.g., 'goldenwings_default') */
  profile?: string;
  /** Table title */
  title?: string;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Callback when row is clicked */
  onRowClick?: (record: any) => void;
  /** Additional CSS class */
  className?: string;
  /** Custom renderers for specific columns */
  customRenderers?: Record<
    string,
    (value: any, record: any) => React.ReactNode
  >;
}

export const UniversalTable: React.FC<UniversalTableProps> = ({
  collection,
  profile,
  title,
  searchPlaceholder = 'Search...',
  onRowClick,
  className,
  customRenderers = {},
}) => {
  const { formatDateTime } = useUserTimezone();
  const {
    data,
    header,
    columns: udcColumns,
    summary,
    availableFilters,
    activeFilters,
    isLoading,
    error,
    setPage,
    setPerPage,
    setSearch,
    setFilters,
    setOrder,
    clearFilters,
    selectionId,
  } = useUDCTable(collection, profile ? { profile } : {});

  const {
    selectItems,
    deselectItems,
    selectAll,
    clearSelection,
    executeBulkAction,
  } = useUDCSelection(collection, selectionId);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [currentSortOrder, setCurrentSortOrder] = useState<string>('');

  // Sync URL params with table state on mount
  useEffect(() => {
    const page = searchParams.get('page');
    const perPage = searchParams.get('per_page');
    const search = searchParams.get('search');
    const order = searchParams.get('order');

    if (page || perPage || search || order) {
      // Only update if params exist in URL
      const updates: any = {};
      if (page) updates.page = parseInt(page);
      if (perPage) updates.per_page = parseInt(perPage);
      if (search) {
        updates.search = search;
        setSearchValue(search);
      }
      if (order) updates.order = order;

      // Note: Filter sync would require more complex parsing
      // For now, we skip filter URL sync (can be added later)
    }
  }, []); // Run only on mount

  // Update URL when table state changes
  useEffect(() => {
    const params: any = {};

    if (summary?.page && summary.page !== 1) {
      params.page = summary.page.toString();
    }

    if (summary?.per_page && summary.per_page !== 20) {
      params.per_page = summary.per_page.toString();
    }

    if (searchValue) {
      params.search = searchValue;
    }

    if (currentSortOrder) {
      params.order = currentSortOrder;
    }

    // Update URL without triggering navigation
    setSearchParams(params, { replace: true });
  }, [summary?.page, summary?.per_page, searchValue, currentSortOrder]);

  // Handle column visibility and order changes
  const handleColumnsChange = useCallback(
    (visible: string[], order: string[]) => {
      setVisibleColumns(visible);
      setColumnOrder(order);
    },
    []
  );

  // Convert UDC columns to Ant Design columns
  const antColumns: ColumnsType<any> = useMemo(() => {
    // Filter visible columns and sort by order
    const orderedColumns =
      columnOrder.length > 0
        ? columnOrder
            .map((name) => udcColumns.find((c) => c.name === name))
            .filter((c): c is UDCColumnDefinition => c !== undefined)
        : udcColumns;

    const columnsToDisplay =
      visibleColumns.length > 0
        ? orderedColumns.filter((col) => visibleColumns.includes(col.name))
        : orderedColumns;

    return columnsToDisplay.map((col: UDCColumnDefinition) => {
      // Debug logging for title column
      if (col.name === 'title') {
        console.log('📊 Title Column Backend Config:', {
          name: col.name,
          display_name: col.display_name,
          width: col.width,
          max_char_length: col.max_char_length,
          overflow_char_policy: col.overflow_char_policy,
          show_full_content_on_hover: col.show_full_content_on_hover,
          sortable: col.sortable,
          type: col.type,
        });
      }

      const column: any = {
        key: col.name,
        dataIndex: col.name,
        title: col.display_name,
        width: col.width,
        sorter: col.sortable,

        // Apply ellipsis if configured in profile
        // Ant Design will automatically apply CSS: overflow: hidden, white-space: nowrap, text-overflow: ellipsis
        ...(col.overflow_char_policy === 'ellipsis' && {
          ellipsis:
            col.show_full_content_on_hover !== false
              ? {
                  showTitle: true, // Show full text in tooltip on hover
                }
              : true,
        }),
      };

      // Debug final column config for title
      if (col.name === 'title') {
        console.log('🎨 Final Ant Design Column Config for Title:', column);
      }

      // Add render function to column
      column.render = (value: any, record: any) => {
        // Check for custom renderer
        if (customRenderers[col.name]) {
          return customRenderers[col.name](value, record);
        }

        // Default rendering based on type
        if (value === null || value === undefined) {
          return '-';
        }

        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }

        // Lookup fields (enum values)
        if (col.type === 'lookup2') {
          if (col.name === 'priority') {
            return <Tag color={getPriorityColor(value)}>{value}</Tag>;
          }
          if (col.name === 'status') {
            return <Tag color={getStatusColor(value)}>{value}</Tag>;
          }
          return <Tag>{value}</Tag>;
        }

        // Date fields - convert UTC to user timezone
        if (col.type === 'date' || col.type === 'datetime') {
          if (!value) return '-';
          return formatDateTime(value);
        }

        return value;
      };

      return column;
    });
  }, [udcColumns, customRenderers, visibleColumns, columnOrder]);

  // Row selection configuration
  const rowSelection = header?.bulk_select?.enabled
    ? {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[], selectedRows: any[]) => {
          setSelectedRowKeys(selectedKeys);

          // Determine if items were selected or deselected
          const itemIds = selectedRows.map((row) => row.id);

          if (selectedKeys.length > selectedRowKeys.length) {
            // Items were selected
            const newIds = itemIds.filter(
              (id: string) => !selectedRowKeys.includes(id)
            );
            if (newIds.length > 0) {
              selectItems(newIds);
            }
          } else if (selectedKeys.length < selectedRowKeys.length) {
            // Items were deselected
            const removedIds = selectedRowKeys.filter(
              (id) => !selectedKeys.includes(id)
            ) as string[];
            if (removedIds.length > 0) {
              deselectItems(removedIds);
            }
          }
        },
        getCheckboxProps: (record: any) => ({
          disabled: record.__meta__?.selectable === false,
        }),
      }
    : undefined;

  // Pagination configuration
  const pagination = summary
    ? {
        current: summary.page,
        pageSize: summary.per_page,
        total: summary.total_count,
        showSizeChanger: true,
        showTotal: (total: number) => `Total ${total} items`,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: (page: number, pageSize: number) => {
          setPage(page);
          if (pageSize !== summary.per_page) {
            setPerPage(pageSize);
          }
        },
      }
    : false;

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    // Debounce search (simple implementation)
    const timer = setTimeout(() => {
      setSearch(value);
    }, 300);

    return () => clearTimeout(timer);
  };

  // Handle sort change
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter && sorter.field) {
      const order =
        sorter.order === 'descend' ? `-${sorter.field}` : sorter.field;
      setOrder(order);
      setCurrentSortOrder(order);
    } else {
      // Clear sort
      setOrder('');
      setCurrentSortOrder('');
    }
  };

  // Handle filter apply
  const handleApplyFilters = (filters: UDCFilter[], usage?: string) => {
    setFilters(filters, usage);
  };

  // Handle remove single filter
  const handleRemoveFilter = (filterId: string) => {
    const newFilters = activeFilters.filter((f) => f.id !== filterId);
    if (newFilters.length === 0) {
      clearFilters();
    } else {
      // Rebuild usage string
      const usage = newFilters.map((f) => f.id).join(' and ');
      setFilters(newFilters, usage);
    }
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    clearFilters();
  };

  // Handle select all
  const handleSelectAll = () => {
    selectAll();
    // Will trigger refetch which updates selectedRowKeys via __meta__.selected
  };

  // Handle clear selection
  const handleClearSelection = () => {
    clearSelection();
    setSelectedRowKeys([]);
  };

  // Handle bulk action execution
  const handleExecuteAction = (actionKey: string, action: any) => {
    const { url, method, action: actionType } = action;

    if (actionType === 'NAVIGATE') {
      // Navigate to URL (replace selection_ids placeholder)
      const targetUrl = url.replace(
        '{selection_ids}',
        selectedRowKeys.join(',')
      );
      window.location.href = targetUrl;
      return;
    }

    // Execute API action
    if (url && method) {
      executeBulkAction({
        url,
        method,
        data: { selection_id: selectionId },
      });
    }
  };

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error instanceof Error ? error.message : 'Unknown error'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className={`universal-table ${className || ''}`}>
      {/* Title and total count */}
      {title && (
        <h1 className="universal-table-title">
          {title}{' '}
          <span className="universal-table-count">
            {summary?.total_count || 0}
          </span>
        </h1>
      )}

      {/* Toolbar */}
      <Space className="universal-table-toolbar" size="middle">
        <Input
          placeholder={searchPlaceholder}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={handleSearchChange}
          style={{ width: 300 }}
          allowClear
        />
        <Badge count={activeFilters.length} offset={[-5, 5]}>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setFilterDrawerOpen(true)}
          >
            Filter
          </Button>
        </Badge>
        <ColumnSelector
          columns={udcColumns}
          collection={collection}
          onColumnsChange={handleColumnsChange}
        />
      </Space>

      {/* Active Filters */}
      <ActiveFilters
        filters={activeFilters}
        availableFilters={availableFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
      />

      {/* Bulk Action Bar */}
      {header?.bulk_select && (
        <BulkActionBar
          bulkSelect={header.bulk_select}
          totalCount={summary?.total_count || 0}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          onExecuteAction={handleExecuteAction}
        />
      )}

      {/* Filter Builder Drawer */}
      <FilterBuilder
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        availableFilters={availableFilters}
        activeFilters={activeFilters}
        onApply={handleApplyFilters}
      />

      {/* Table */}
      {isLoading ? (
        <TableSkeleton
          rows={summary?.per_page || 20}
          columns={udcColumns.length || 5}
          showCheckbox={!!header?.bulk_select?.enabled}
        />
      ) : (
        <Table
          loading={false}
          columns={antColumns}
          dataSource={data}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={pagination}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => onRowClick?.(record),
            style: { cursor: onRowClick ? 'pointer' : 'default' },
          })}
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
};

// Helper functions

function getPriorityColor(priority: string): string {
  const colorMap: Record<string, string> = {
    CRITICAL: 'red',
    HIGH: 'orange',
    NORMAL: 'gold',
    LOW: 'green',
  };
  return colorMap[priority] || 'default';
}

function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    NEW: 'blue',
    IN_PROGRESS: 'cyan',
    RESOLVED: 'green',
    CLOSED: 'default',
    CANCELLED: 'red',
  };
  return colorMap[status] || 'default';
}

// Removed formatDate helper - now using useUserTimezone hook for timezone-aware formatting
